import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { ApiError } from '../../api/client';
import { LocationPicker } from '../locations/LocationPicker';
import { transfer } from '../stock/api';
import type { SerialUnit } from './api';

/**
 * Moving named units from one shelf to another, several at a time.
 *
 * A whole drawer gets rehoused at once — a cabinet is emptied, a bench is
 * cleared — and doing that one unit at a time through the posting form means
 * finding each unit, then typing the same destination again.
 *
 * The units chosen need not share a shelf, and that is the only thing here that
 * is not obvious: a transfer is *from* somewhere *to* somewhere, so a selection
 * spanning three shelves is three transfers. They are grouped by where each unit
 * currently is and posted one group at a time; the ledger then reads the way it
 * should, with each shelf losing exactly what left it.
 */
export function MoveUnits({
  componentId,
  chosen,
  onDone,
  onCancel,
}: {
  componentId: string;
  chosen: SerialUnit[];
  onDone: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [target, setTarget] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Every unit that is physically somewhere, gathered by where that is. A unit
  // with no place has been issued or written off; moving it would be a claim
  // about a shelf it is not on.
  const byPlace = new Map<string, SerialUnit[]>();
  for (const unit of chosen) {
    if (!unit.current_location_id) continue;
    const group = byPlace.get(unit.current_location_id) ?? [];
    group.push(unit);
    byPlace.set(unit.current_location_id, group);
  }

  const placeless = chosen.length - [...byPlace.values()].reduce((sum, g) => sum + g.length, 0);

  const move = useMutation({
    mutationFn: async () => {
      // Sequentially, not in parallel: each posting writes to the same two
      // balances, and the server is one connection per request — firing eight
      // at once buys nothing and makes a partial failure harder to read.
      for (const [from, units] of byPlace) {
        await transfer({
          component_id: componentId,
          from_location_id: from,
          to_location_id: target,
          quantity: String(units.length),
          note: note || null,
          serial_unit_ids: units.map((unit) => unit.id),
        });
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['serials'] });
      await queryClient.invalidateQueries({ queryKey: ['stock'] });
      await queryClient.invalidateQueries({ queryKey: ['components'] });
      onDone();
    },
    onError: (exc: unknown) => setError(exc instanceof ApiError ? exc.code : 'unknown'),
  });

  return (
    <div className="move-units">
      <div className="move-units__row">
        <strong className="move-units__count">
          {t('serials.move.chosen', { count: chosen.length })}
        </strong>

        {/* No labels above these two: the bar is a single line of controls that
            appears for as long as something is ticked, and each control says
            what it is inside itself. The picker's one-press places are pushed
            below the line by the stylesheet rather than making its field a head
            taller than the one beside it. */}
        <LocationPicker
          label={t('serials.move.to')}
          value={target}
          onChange={setTarget}
          allowOutside={false}
          componentId={componentId}
          scanTarget
        />

        <input
          value={note}
          onChange={(event) => setNote(event.target.value)}
          placeholder={t('stock.note')}
          aria-label={t('stock.note')}
        />

        <div className="move-units__actions">
          <button
            type="button"
            className="btn btn--primary"
            disabled={!target || byPlace.size === 0 || move.isPending}
            onClick={() => {
              setError(null);
              move.mutate();
            }}
          >
            {t('serials.move.submit', { groups: byPlace.size })}
          </button>
          <button type="button" className="btn" onClick={onCancel}>
            {t('common.cancel')}
          </button>
        </div>
      </div>

      {/* Said before the move, not after: a unit that is already issued cannot
          be taken off a shelf, and silently dropping it would make the count
          disagree with what was ticked. */}
      {placeless > 0 && <p className="hint">{t('serials.move.placeless', { n: placeless })}</p>}
      {byPlace.size > 1 && (
        <p className="hint">{t('serials.move.groups', { n: byPlace.size })}</p>
      )}
      {error && (
        <p className="panel__error" role="alert">
          {t(`serials.errors.${error}`, { defaultValue: t('serials.errors.unknown') })}
        </p>
      )}
    </div>
  );
}
